import React, { useEffect, useState } from 'react';
import {
  StyledTableContainer,
  StyledTableHeadRow,
  StyledTableCellHead,
  StyledTableRow,
  StyledTableCellBody,
  StyledTypography,
} from './ResultTable.Styled';
import { Table, TableBody, TableHead } from '@mui/material';
import TextField from '@mui/material/TextField';
import { ReviewTableDeleteIcon } from '../../assets/images/UI-dashboard';
import { useDispatch } from 'react-redux';
import { DataRow, staticAudioResultData, staticInputResultData } from '../../types/common';
import ConfirmationDialog from '../UI/ConfirmationDialog';
import ErrorPopup from '../ErrorPopup/ErrorPopup';
import { useAppSelector } from '../../state/hooks';
import { RootState } from '../../state/store';
import { deleteMappingRow, editMappingRow } from '../../features/SpeechRecognition/audioSlice';
import {
  deleteInputMappingRow,
  editInputMappingRow,
} from '../../pages/ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { SCENARIO_CREATION_DATA } from '../../utils/dataConstants';
import { isInvalidColumn } from '../../utils/helper';

const audioMappedTablecolumns = SCENARIO_CREATION_DATA.AUDIO_MAPPING_TABLE_COLUMNS;

const inputMappedTablecolumns = SCENARIO_CREATION_DATA.INPUT_MAPPING_TABLE_COLUMNS;

interface ResultTableProps {
  mappings: Record<string, any>[];
  error: string | null;
}

export const ResultTable: React.FC<ResultTableProps> = ({ mappings, error }) => {
  const dispatch = useDispatch();
  const isAudioMappingTableEnable = useAppSelector((state: RootState) => state.audio.isAudioMappingTableEnable);
  const isReviewTableEnable = useAppSelector((state: RootState) => state.reviewTable.enableReviewTableDialog);
  const audioTableMappings = useAppSelector((state: RootState) => state.audio.mappings);
  const [dataRows, setDataRows] = useState<any[]>(
    mappings.length === 0 ? (isAudioMappingTableEnable ? staticAudioResultData : staticInputResultData) : mappings,
  );
  const [editCell, setEditCell] = useState<{ rowIndex: number; columnId: string; previousValue: string } | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);
  const [errorPopupOpen, setErrorPopupOpen] = useState<boolean>(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState<string | null>(null);

  useEffect(() => {
    if (mappings.length > 0) {
      setDataRows(mappings);
    } else {
      if (isAudioMappingTableEnable) {
        setDataRows(staticAudioResultData);
      } else {
        setDataRows(staticInputResultData);
      }
    }
  }, [audioTableMappings]);

  useEffect(() => {
    if (error) {
      setErrorPopupOpen(true);
    }
  }, [error]);

  const handleEditCell = (rowIndex: number, columnId: string) => {
    setEditCell({
      rowIndex,
      columnId,
      previousValue: dataRows[rowIndex][columnId],
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditCell((prev) => (prev ? { ...prev, previousValue: event.target.value } : prev));
  };

  const handleBlur = () => {
    if (!editCell) return;

    const { rowIndex, columnId, previousValue } = editCell;
    const numValue = parseFloat(previousValue);
    const minLimit = dataRows[rowIndex]?.min_price || 0;
    const maxLimit = dataRows[rowIndex]?.max_price || 0;

    if (columnId === 'New_Price' && (isNaN(numValue) || numValue < minLimit || numValue > maxLimit)) {
      setErrorPopupOpen(true);
      setErrorPopupMessage(`New Price must be between ${minLimit} and ${maxLimit}`);
      setEditCell(null);
      return;
    }

    setDataRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[rowIndex] = { ...newRows[rowIndex], [columnId]: previousValue };
      return newRows;
    });

    const updatedRow = { ...dataRows[rowIndex], [columnId]: previousValue };
    if (isAudioMappingTableEnable) {
      dispatch(editMappingRow({ rowIndex, updatedRow }));
    } else {
      dispatch(editInputMappingRow({ rowIndex, updatedRow }));
    }

    setEditCell(null);
  };

  const isRowRed = (row: any) => {
    return row.Valid === false;
  };

  const handleDeleteClick = (rowIndex: number) => {
    setRowToDelete(rowIndex);
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete !== null) {
      if (isAudioMappingTableEnable) {
        dispatch(deleteMappingRow(rowToDelete));
      } else {
        dispatch(deleteInputMappingRow(rowToDelete));
      }
      setRowToDelete(null);
      setOpenDialog(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRowToDelete(null);
  };

  const handleCloseErrorPopup = () => {
    setErrorPopupOpen(false);
    setErrorPopupMessage(null);
  };

  const formatNumber = (value: any, columnId: string): string => {
    if (columnId === 'Distribution' || columnId === 'New_Distribution' || columnId === 'Current_Distribution') {
      return value;
    }

    const num = parseFloat(value);
    if (!isNaN(num)) {
      return num.toFixed(2);
    }

    return value;
  };

  return (
    <>
      <StyledTableContainer
        style={{
          maxHeight: !isReviewTableEnable ? 200 : 500,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <StyledTableHeadRow>
              {(isAudioMappingTableEnable ? audioMappedTablecolumns : inputMappedTablecolumns).map((column) => (
                <StyledTableCellHead
                  align="center"
                  key={column.id}
                  style={{
                    borderBottom: '1px solid #ddd',
                    borderRight: '1px solid #ddd',
                    backgroundColor: '#f7f7f7',
                    zIndex: 2,
                  }}
                >
                  <StyledTypography sx={{ whiteSpace: column.label === 'New Price' ? 'nowrap' : 'inherit' }}>
                    {column.label}
                  </StyledTypography>
                </StyledTableCellHead>
              ))}
            </StyledTableHeadRow>
          </TableHead>
          <TableBody>
            {dataRows.map((row: DataRow, rowIndex: number) => (
              <StyledTableRow
                key={rowIndex}
                style={{
                  backgroundColor: isRowRed(row) ? '#ffebeb' : 'inherit',
                  color: isRowRed(row) ? '#E81C0E' : 'inherit',
                }}
              >
                {(isAudioMappingTableEnable ? audioMappedTablecolumns : inputMappedTablecolumns).map((column) => (
                  <StyledTableCellBody
                    align="center"
                    padding="none"
                    key={column.id}
                    style={{
                      borderBottom: '1px solid #ddd',
                      borderRight: '1px solid #ddd',
                    }}
                  >
                    <StyledTypography
                      style={{
                        paddingLeft: '15px',
                        paddingRight: '15px',
                        color: isInvalidColumn(row, column.id) ? '#E81C0E' : 'inherit',
                        fontWeight: isInvalidColumn(row, column.id) ? 700 : 600,
                      }}
                    >
                      {column.id === 'actions' ? (
                        <div>
                          <img
                            onClick={() => {
                              if (mappings.length !== 0) {
                                handleDeleteClick(rowIndex);
                              }
                            }}
                            src={ReviewTableDeleteIcon}
                            alt="delete-icon"
                            style={{
                              cursor: 'pointer',
                            }}
                          />
                        </div>
                      ) : column.id !== 'actions' && column.id !== 'Base_Price' && column.id !== 'Action' ? (
                        editCell?.rowIndex === rowIndex && editCell.columnId === column.id ? (
                          <TextField
                            value={editCell.previousValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="standard"
                            fullWidth
                            autoFocus
                            style={{
                              backgroundColor: isRowRed(row) ? '#ffebeb' : 'inherit',
                              color: isRowRed(row) ? '#E81C0E' : 'inherit',
                              textAlign: 'center',
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                            InputProps={{
                              style: {
                                textAlign: 'center',
                                color: isRowRed(row) ? '#E81C0E' : 'inherit',
                                fontWeight: isRowRed(row) ? 700 : 600,
                                fontSize: 12,
                              },
                            }}
                          />
                        ) : (
                          <button
                            onClick={() => handleEditCell(rowIndex, column.id)}
                            style={{
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 12,
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              font: 'inherit',
                            }}
                          >
                            {formatNumber(row[column.id as keyof DataRow], column.id) || '--'}
                          </button>
                        )
                      ) : (
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        >
                          {formatNumber(row[column.id as keyof DataRow], column.id) || '--'}
                        </span>
                      )}
                    </StyledTypography>
                  </StyledTableCellBody>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <ConfirmationDialog open={openDialog} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} />
      <ErrorPopup
        open={errorPopupOpen}
        message={errorPopupMessage || error || 'An unknown error occurred'}
        onClose={handleCloseErrorPopup}
      />
    </>
  );
};
